import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { RotateCw, Share2, Swords, Trophy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { tierColor, msColor } from "../lib/tiers";
import ScoreGauge from "./ScoreGauge";
import api from "../lib/api";

function Stat({ label, value, unit, color, testid }) {
  return (
    <div className="card-surface rounded-2xl p-5 text-center" data-testid={testid}>
      <div className="text-white/45 text-xs uppercase tracking-widest mb-2">{label}</div>
      <div className="font-mono font-extrabold text-2xl" style={{ color: color || "#fff" }}>
        {value}<span className="text-sm text-white/40 ml-1">{unit}</span>
      </div>
    </div>
  );
}

export default function ResultReveal({ result, onTryAgain }) {
  const navigate = useNavigate();
  const color = tierColor(result.tier);

  const share = async () => {
    const text = `⚡ I got ${result.best_ms}ms on ReactionLab — faster than ${result.percentile}% of players. Can you beat me?`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "ReactionLab", text, url: window.location.origin });
      } else {
        await navigator.clipboard.writeText(`${text} ${window.location.origin}`);
        toast.success("Result copied to clipboard!");
      }
    } catch {}
  };

  const challenge = async () => {
    try {
      const { data } = await api.post("/challenge", { test_type: result.test_type, target_ms: result.best_ms });
      navigate(`/challenge/${data.code}?created=1`);
    } catch {
      toast.error("Could not create challenge");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="w-full max-w-3xl mx-auto" data-testid="result-screen"
    >
      {/* Hero score */}
      <div className="text-center mb-10">
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 18 }}>
          <div data-testid="result-best-ms" className="font-mono font-extrabold text-[22vw] sm:text-8xl leading-none glow-green" style={{ color }}>
            {result.best_ms}<span className="text-3xl sm:text-4xl text-white/40 ml-2">ms</span>
          </div>
        </motion.div>
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
          <div data-testid="result-tier" className="font-display font-black text-2xl sm:text-3xl mt-2 tracking-tight" style={{ color }}>
            {result.tier_label}
          </div>
          <div data-testid="result-percentile" className="text-white/60 mt-2">
            Faster than <span className="font-bold text-white">{result.percentile}%</span> of ReactionLab players
            {result.estimated_percentile && <span className="text-white/35 text-xs ml-1">(estimated)</span>}
          </div>
          <p className="text-white/40 text-sm mt-2 max-w-md mx-auto">{result.encouragement}</p>
        </motion.div>
      </div>

      {result.new_personal_best && (
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.7, type: "spring" }}
          className="flex items-center justify-center gap-2 mb-6 mx-auto w-fit px-5 py-2 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30" data-testid="personal-best-banner">
          <Trophy size={18} className="text-[#39FF14]" />
          <span className="font-display font-bold text-[#39FF14]">NEW PERSONAL BEST</span>
        </motion.div>
      )}

      {result.achievements_unlocked?.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-center mb-6" data-testid="unlocked-achievements">
          {result.achievements_unlocked.map((a) => (
            <span key={a.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm">
              <Sparkles size={14} className="text-[#39FF14]" /> {a.name}
            </span>
          ))}
        </div>
      )}

      {/* Score + stats */}
      <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center mb-8">
        <div className="flex justify-center">
          <ScoreGauge score={result.performance_score} tier={result.tier} size={180} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat testid="stat-average" label="Average" value={result.average_ms} unit="ms" />
          <Stat testid="stat-median" label="Median" value={result.median_ms} unit="ms" />
          <Stat testid="stat-best" label="Best" value={result.best_ms} unit="ms" color={color} />
          <Stat testid="stat-slowest" label="Slowest" value={result.worst_ms} unit="ms" color={msColor(result.worst_ms)} />
          <Stat testid="stat-consistency" label="Consistency" value={result.consistency} unit="%" />
          <Stat testid="stat-false-starts" label="False Starts" value={result.false_starts} unit="" color={result.false_starts ? "#F59E0B" : "#fff"} />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button data-testid="try-again-button" onClick={onTryAgain}
          className="btn-glow flex items-center justify-center gap-2 bg-[#39FF14] text-black font-display font-extrabold px-8 py-4 rounded-full">
          <RotateCw size={18} /> TRY AGAIN
        </button>
        <button data-testid="share-result-button" onClick={share}
          className="flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition">
          <Share2 size={18} /> SHARE
        </button>
        <button data-testid="challenge-friend-button" onClick={challenge}
          className="flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition">
          <Swords size={18} /> CHALLENGE
        </button>
      </div>
    </motion.div>
  );
}
